import { createContext, useContext } from 'react';
import toastr from 'toastr';
import 'toastr/build/toastr.min.css';

// Configure toastr defaults
toastr.options = {
  closeButton: true,
  debug: false,
  newestOnTop: true,
  progressBar: true,
  positionClass: 'toast-top-right',
  preventDuplicates: false,
  onclick: null,
  showDuration: 300,
  hideDuration: 1000,
  timeOut: 5000,
  extendedTimeOut: 1000,
  showEasing: 'swing',
  hideEasing: 'linear',
  showMethod: 'fadeIn',
  hideMethod: 'fadeOut'
};

const NotificationContext = createContext(null);

/**
 * NotificationProvider - Wraps toastr library for application-wide notifications
 */
export function NotificationProvider({ children }) {
  /**
   * Show success notification
   * @param {string} message - The message to display
   * @param {string} title - Optional title
   */
  const showSuccess = (message, title = 'Success') => {
    toastr.success(message, title);
  };

  /**
   * Show error notification
   * @param {string} message - The message to display
   * @param {string} title - Optional title
   */
  const showError = (message, title = 'Error') => {
    toastr.error(message, title);
  };

  /**
   * Show warning notification
   * @param {string} message - The message to display
   * @param {string} title - Optional title
   */
  const showWarning = (message, title = 'Warning') => {
    toastr.warning(message, title);
  };

  /**
   * Show info notification
   * @param {string} message - The message to display
   * @param {string} title - Optional title
   */
  const showInfo = (message, title = 'Info') => {
    toastr.info(message, title);
  };

  /**
   * Clear all notifications
   */
  const clear = () => {
    toastr.clear();
  };

  /**
   * Remove a specific notification
   * @param {object} toast - The toast object returned from toastr
   */
  const remove = (toast) => {
    toastr.remove(toast);
  };

  const value = {
    showSuccess,
    showError,
    showWarning,
    showInfo,
    clear,
    remove
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
}

/**
 * useNotification hook - Access notification context
 *
 * @example
 * const { showSuccess, showError } = useNotification();
 * showSuccess('Card saved successfully!');
 * showError('Failed to save card', 'Error');
 */
export function useNotification() {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotification must be used within NotificationProvider');
  }
  return context;
}

export default NotificationContext;
